import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { getScreenshotRevealKey } from '@/utils/screenshotPrivacy';
import { TimelineView } from './Timeline.view';
import {
  DEMO_SESSIONS_BY_DAY,
  DEMO_MEMBERS_OPTIONS,
  DEMO_SESSION_SCREENSHOTS,
} from './Timeline.demoData';

const DEMO_TIMEZONE = 'America/New_York';
const DEMO_TEAM_NAME = 'Engineering';

function buildCsvFromDemoDays(days, tz) {
  const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const rows = [];
  (days || []).forEach((day) => {
    day.sessions.forEach((s) => {
      const { displayIdle: displayIdleSec, idlePercent } =
        normalizeIdleForDisplay(s.durationSec, s.idleSec);
      const productiveSec =
        s.productiveSec != null
          ? s.productiveSec
          : Math.max(0, (s.durationSec || 0) - displayIdleSec);
      rows.push({
        Date: day.dayLabel,
        Start: moment(s.start).tz(tz).format('hh:mm A'),
        End: s.end ? moment(s.end).tz(tz).format('hh:mm A') : '-',
        Duration: formatTimeFromSeconds(s.durationSec),
        'Idle time': formatTimeFromSeconds(displayIdleSec),
        'Productive time': formatTimeFromSeconds(productiveSec),
        'Idle %': formatPercent(idlePercent),
        'Activity %': formatPercent(s.activityPct),
        Project: s.project ?? '',
        Task: s.task ?? '',
      });
    });
  });
  const header = Object.keys(rows[0] || {});
  const lines = [
    header.join(','),
    ...rows.map((r) => header.map((h) => escape(r[h])).join(',')),
  ];
  return lines.join('\n');
}

export const TimelineDemoComponent = () => {
  const [selectedMember, setSelectedMember] = useState(DEMO_MEMBERS_OPTIONS[0]);
  const [selectedTimezone, setSelectedTimezone] = useState(DEMO_TIMEZONE);
  const [range, setRange] = useState({
    start: DEMO_SESSIONS_BY_DAY[0]?.dayKey,
    end: DEMO_SESSIONS_BY_DAY[0]?.dayKey,
  });
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [activeSessionMeta, setActiveSessionMeta] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);

  const closeScreenshotsModal = () => {
    setScreenshotModalOpen(false);
    setActiveSessionMeta(null);
    setSelectedScreenshotIndex(0);
  };

  const openScreenshotsModal = (session, dayLabel) => {
    setActiveSessionMeta({
      sessionId: session?.id,
      dayLabel,
      start: session?.start,
      end: session?.end,
    });
    setSelectedScreenshotIndex(0);
    setScreenshotModalOpen(true);
  };

  const flattenedScreens = useMemo(() => {
    const demoRows =
      DEMO_SESSION_SCREENSHOTS[activeSessionMeta?.sessionId] || [];
    return demoRows.map((row) => ({
      ...row,
      revealKey: getScreenshotRevealKey(row),
    }));
  }, [activeSessionMeta]);

  const employeeName = selectedMember?.label?.replace(/\s*\([^)]*\)\s*$/, '');

  const onExport = () => {
    const csv = buildCsvFromDemoDays(DEMO_SESSIONS_BY_DAY, selectedTimezone);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'timeline_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <TimelineView
      membersOptions={DEMO_MEMBERS_OPTIONS}
      selectedMember={selectedMember}
      onMemberFilterChange={setSelectedMember}
      membersLoading={false}
      membersError={false}
      membersErrorObj={null}
      refetchMembers={() => {}}
      timezoneOptions={[{ value: DEMO_TIMEZONE, label: DEMO_TIMEZONE }]}
      selectedTimezone={selectedTimezone}
      onTimezoneChange={setSelectedTimezone}
      timezonesLoading={false}
      date={range.start}
      onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
      onRefresh={() => {}}
      onExport={onExport}
      exportLoading={false}
      exportDisabled={!DEMO_SESSIONS_BY_DAY.length}
      loading={false}
      sessionsByDay={DEMO_SESSIONS_BY_DAY}
      onOpenScreenshotsModal={openScreenshotsModal}
      screenshotModalOpen={screenshotModalOpen}
      closeScreenshotsModal={closeScreenshotsModal}
      activitiesLoading={false}
      activitiesError=""
      flattenedScreens={flattenedScreens}
      selectedScreenshotIndex={selectedScreenshotIndex}
      setSelectedScreenshotIndex={setSelectedScreenshotIndex}
      activeSessionMeta={activeSessionMeta}
      employeeName={employeeName}
      teamName={DEMO_TEAM_NAME}
    />
  );
};
